import type { TFunction } from 'i18next';
import type {
  CancellationDiscountDuration,
  CancellationOfferPage,
  PortalSubscription,
} from '@api/customer-portal-api';

// Helpers for the discount offer page: a percentage off future renewals, applied by the
// backend as a Whop promo code. The walk treats it like a pause page (./retentionFlow).

export interface DiscountTerms {
  percentage: number;
  duration: CancellationDiscountDuration;
  months: number | null; // "repeating" only
}

export const isDiscountPage = (page: CancellationOfferPage | null | undefined): boolean =>
  page?.offer_type === 'discount';

const DURATIONS: readonly string[] = ['once', 'repeating', 'forever'];

const isWholeNumber = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

const parseTerms = (fields: {
  discount_percentage?: unknown;
  discount_duration?: unknown;
  discount_duration_months?: unknown;
}): DiscountTerms | null => {
  const { discount_percentage: percentage, discount_duration: duration } = fields;
  if (!isWholeNumber(percentage, 1, 100)) return null;
  if (typeof duration !== 'string' || !DURATIONS.includes(duration)) return null;
  if (duration !== 'repeating') {
    return { percentage, duration: duration as CancellationDiscountDuration, months: null };
  }
  const months = fields.discount_duration_months;
  return isWholeNumber(months, 1, Number.MAX_SAFE_INTEGER)
    ? { percentage, duration: 'repeating', months }
    : null;
};

// The page's offer, or null when it can't be described (the accept is then hidden, so a
// customer never accepts terms they weren't shown).
export const discountTermsOf = (
  page: CancellationOfferPage | null | undefined,
): DiscountTerms | null => (page && isDiscountPage(page) ? parseTerms(page) : null);

// The terms Whop confirmed, from the accept's 200 response; the page's terms otherwise.
export const discountTermsFromResponse = (
  fallback: DiscountTerms,
  response: unknown,
): DiscountTerms =>
  (response && typeof response === 'object' ? parseTerms(response) : null) ?? fallback;

export interface NextChargeDiscount {
  terms: DiscountTerms;
  fullCents: number;
  discountedCents: number;
}

// The retention discount on a subscription's next charge, from the backend's
// active_discount note (it decides when the note applies). null when there is none, or
// when the note wouldn't lower the price or its terms can't be described.
export const nextChargeDiscount = (
  sub: Pick<PortalSubscription, 'active_discount'>,
): NextChargeDiscount | null => {
  const note = sub.active_discount;
  if (!note) return null;
  const terms = parseTerms(note);
  const fullCents = note.full_amount_cents;
  const discountedCents = note.discounted_amount_cents;
  if (!terms || !isWholeNumber(discountedCents, 0, fullCents - 1)) return null;
  return { terms, fullCents, discountedCents };
};

// The discount on a price shown as recurring ("$42.39 / month"). A one-time discount
// covers only the next charge, so recurring prices keep the full amount for it.
export const recurringPriceDiscount = (
  sub: Pick<PortalSubscription, 'active_discount'>,
): NextChargeDiscount | null => {
  const discount = nextChargeDiscount(sub);
  return discount && discount.terms.duration !== 'once' ? discount : null;
};

// "on your next renewal", "for 3 months", "on every renewal".
export const formatDiscountDuration = (t: TFunction, terms: DiscountTerms): string =>
  terms.duration === 'repeating'
    ? t('retention.discount.duration_repeating', { count: terms.months ?? 0 })
    : t(`retention.discount.duration_${terms.duration}`);

// Accept button label: "Try again" after an unconfirmed accept (retrying is safe: the
// backend checks Whop first and never applies the discount twice).
export const discountAcceptLabel = (
  t: TFunction,
  state: {
    busy: boolean;
    unconfirmed: boolean;
    acceptLabel?: string | null;
    percentage: number;
  },
): string =>
  state.busy
    ? t('common.pleaseWait')
    : state.unconfirmed
      ? t('retention.discount.tryAgain')
      : state.acceptLabel || t('retention.discount.accept', { percent: state.percentage });

// How a failed accept is shown. The backend's errors carry no codes, so this branches on
// the HTTP status: "unavailable" = 409: a short message, then the next page;
// "unconfirmed" = 502 (also 503, 504 or no answer): the discount may or may not be on,
// so "Try again" re-sends the same request; "failed" = anything else (400/403/404/500):
// the API's own error message.
export type DiscountAcceptFailure = 'unavailable' | 'unconfirmed' | 'failed';

export const classifyDiscountAcceptError = (err: unknown): DiscountAcceptFailure => {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === 409) return 'unavailable';
  if (status === undefined || status === 502 || status === 503 || status === 504) {
    return 'unconfirmed';
  }
  return 'failed';
};

// A 409 after an unconfirmed accept may mean the earlier attempt went through: the backend
// then counts the offer as used. The refreshed subscription settles it, since the backend
// sends active_discount only once the discount is applied. A failed refresh leaves it
// unknown, so the customer is never told the offer is gone when it may be on.
export type DiscountConflictOutcome =
  | { kind: 'applied'; terms: DiscountTerms }
  | { kind: 'unavailable' }
  | { kind: 'unconfirmed' };

export const settleDiscountConflict = (refresh: {
  failed: boolean;
  sub: Pick<PortalSubscription, 'active_discount'> | null | undefined;
}): DiscountConflictOutcome => {
  if (refresh.failed) return { kind: 'unconfirmed' };
  const discount = refresh.sub ? nextChargeDiscount(refresh.sub) : null;
  return discount ? { kind: 'applied', terms: discount.terms } : { kind: 'unavailable' };
};
